import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_URL from '../../config/api';
import { Users, Plus, Edit2, Trash2, Eye, EyeOff } from 'lucide-react';
import CustomAlert from './shared/CustomAlert';

// ⭐ Plantillas de rol para restaurante: pre-llenan los permisos con un
// combo tipico, pero siguen siendo editables despues (no atan al empleado
// a la plantilla para siempre, solo la usan como punto de partida y como
// nombre de puesto para mostrar en el badge del POS).
const ROLE_TEMPLATES = {
  mesero: {
    label: 'Mesero',
    description: 'Toma pedidos y gestiona mesas',
    permissions: { caja: false, productos: false, categorias: false, mesas: true, pedidos: true, cocina: false, reportes: false, paginas: false, crm: false, remarketing: false }
  },
  cajero: {
    label: 'Cajero',
    description: 'Cobra, gestiona pedidos y mesas',
    permissions: { caja: true, productos: false, categorias: false, mesas: true, pedidos: true, cocina: false, reportes: false, paginas: false, crm: false, remarketing: false }
  },
  cocinero: {
    label: 'Cocinero',
    description: 'Solo ve la pantalla de Cocina (modo pantalla completa)',
    permissions: { caja: false, productos: false, categorias: false, mesas: false, pedidos: false, cocina: true, reportes: false, paginas: false, crm: false, remarketing: false }
  },
  gerente: {
    label: 'Gerente',
    description: 'Acceso operativo completo, sin gestionar empleados',
    // ⭐ "Completo" ahora sí incluye CRM/Contactos (antes no existía el
    // checkbox, ver permissionLabels) -- un Gerente es el puesto más
    // razonable para heredar esto por defecto sin que el dueño tenga que
    // tocarlo a mano cada vez.
    permissions: { caja: true, productos: true, categorias: true, mesas: true, pedidos: true, cocina: true, reportes: true, paginas: true, crm: true, remarketing: true }
  },
  personalizado: {
    label: 'Personalizado',
    description: 'Elige los permisos manualmente',
    permissions: null
  }
};


function EmpleadosManager({ token, clientData }) {
  const [employees, setEmployees] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [customAlert, setCustomAlert] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  // ⭐ Límites por plan
  const PLAN_LIMITS = {
    basic: { employees: 3, tables: 6 },
    standard: { employees: 8, tables: 15 },
    premium: { employees: 20, tables: 30 },
    enterprise: { employees: Infinity, tables: Infinity }
  };

  // ⭐ Límite de empleados para cuentas CRM-only (2026-08-08, mismo pedido
  // que destrabó el tab -- ver DashboardPOS.js/CRM_EMPLOYEES_MIN_TIER).
  // clientData?.plan (POS) NO aplica acá -- para estas cuentas queda fijo
  // en 'basico' desde el registro standalone (nunca tuvieron punto de
  // venta), así que usar PLAN_LIMITS[plan] de abajo siempre caería al
  // límite más chico sin importar el tier real de CRM contratado. Se
  // decide por crm_plan_tier en su lugar. 'emprendedor' no debería llegar
  // nunca acá (el tab ya está oculto para ese tier), pero se deja en 0 por
  // seguridad si de algún modo se activa el tab manualmente. Números (5
  // para IA Plus) son un punto de partida razonable, no vienen de un
  // pricing publicado con esa cifra exacta -- ajustar si Angelo pide otra.
  const CRM_EMPLOYEE_LIMITS = { emprendedor: 0, ia_plus: 5, business: Infinity };

  const [newEmployee, setNewEmployee] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    role: 'personalizado',
    permissions: {
      caja: true,
      productos: false,
      categorias: false,
      mesas: true,
      pedidos: true,
      cocina: true,
      reportes: false,
      paginas: false,
      // ⭐ Default false a propósito: acceso al CRM/Contactos se asigna
      // manualmente, no viene por defecto con ningún puesto (ver nota en
      // permissionLabels más abajo).
      crm: false,
      remarketing: false
    },
    active: true
  });

  const applyRoleTemplate = (key) => {
    const template = ROLE_TEMPLATES[key];
    if (!template) return;
    setNewEmployee(prev => ({
      ...prev,
      role: key,
      permissions: template.permissions ? { ...template.permissions } : prev.permissions
    }));
  };

  const showAlert = (type, title, message) => {
    setCustomAlert({ type, title, message });
  };

  useEffect(() => {
    loadEmployees();
  }, []);

  const loadEmployees = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/pos/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(response.data);
    } catch (error) {
      console.error('Error al cargar empleados:', error);
    }
  };

  const handleCreateEmployee = async () => {
    if (!newEmployee.name.trim()) {
      showAlert('warning', 'Campo Requerido', ' El nombre es requerido');
      return;
    }

    if (!newEmployee.email.trim()) {
      showAlert('warning', 'Campo Requerido', ' El email es requerido: el empleado inicia sesión con su email y contraseña');
      return;
    }

    if (!newEmployee.password || newEmployee.password.length < 4) {
      showAlert('warning', 'Contraseña Débil', ' La contraseña debe tener al menos 4 caracteres');
      return;
    }

    try {
      await axios.post(
        `${API_URL}/api/pos/employees`,
        newEmployee,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showAlert('success', 'Empleado Creado', ` Empleado "${newEmployee.name}" creado exitosamente\n\n Email: ${newEmployee.email}\n Contraseña: ${newEmployee.password}`);
      loadEmployees();
      closeModal();
    } catch (error) {
      console.error('Error:', error);
      showAlert('error', 'Error', ` ${error.response?.data?.error || 'No se pudo crear el empleado'}`);
    }
  };

  const handleUpdateEmployee = async () => {
    try {
      await axios.put(
        `${API_URL}/api/pos/employees/${editingEmployee.id}`,
        newEmployee,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showAlert('success', 'Empleado Actualizado', ' Empleado actualizado exitosamente');
      loadEmployees();
      closeModal();
    } catch (error) {
      console.error('Error:', error);
      showAlert('error', 'Error', ` ${error.response?.data?.error || 'No se pudo actualizar el empleado'}`);
    }
  };

  const handleDeleteEmployee = (employee) => {
    setCustomAlert({
      type: 'confirm',
      title: 'Confirmar Eliminación',
      message: `¿Estás seguro de eliminar al empleado "${employee.name}"?\n\nEsta acción no se puede deshacer.`,
      onConfirm: async () => {
        try {
          await axios.delete(`${API_URL}/api/pos/employees/${employee.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          showAlert('success', 'Empleado Eliminado', `️ Empleado "${employee.name}" eliminado`);
          loadEmployees();
        } catch (error) {
          console.error('Error:', error);
          showAlert('error', 'Error', ` ${error.response?.data?.error || 'No se pudo eliminar el empleado'}`);
        }
      }
    });
  };

  const openEditModal = (employee) => {
    setEditingEmployee(employee);
    setNewEmployee({
      name: employee.name,
      email: employee.email || '',
      phone: employee.phone || '',
      password: '', // No mostrar contraseña existente
      // Si el rol guardado no corresponde a ninguna plantilla conocida
      // (ej. empleados creados antes de que existiera este selector, que
      // quedaron con role='employee'), se cae a "Personalizado" en vez de
      // romper o mostrar un valor sin sentido.
      role: ROLE_TEMPLATES[employee.role] ? employee.role : 'personalizado',
      permissions: employee.permissions,
      active: employee.active
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingEmployee(null);
    setShowPassword(false);
    setNewEmployee({
      name: '',
      email: '',
      phone: '',
      password: '',
      role: 'personalizado',
      permissions: {
        caja: true,
        productos: false,
        categorias: false,
        mesas: true,
        pedidos: true,
        cocina: true,
        reportes: false,
        paginas: false,
        editar_pedidos_pagados: false
      },
      active: true
    });
  };

  const togglePermission = (key) => {
    setNewEmployee(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [key]: !prev.permissions[key]
      }
    }));
  };

  const permissionLabels = {
    caja: ' Caja / Ventas',
    productos: ' Productos',
    categorias: '️ Categorías',
    mesas: ' Mesas',
    pedidos: ' Pedidos Pendientes',
    cocina: ' Cocina',
    reportes: ' Reportes',
    // ⭐ Constructor de Páginas Web (2026-08-08). Se agrega acá Y en el gate
    // del tab en DashboardPOS.js al mismo tiempo, a propósito: el backend ya
    // lo exige con requirePermission('paginas'), así que si faltara alguno de
    // los dos lados quedaría como un checkbox que no hace nada -- justo el
    // problema que hubo antes con pendientes/pagos/configuracion.
    paginas: ' Páginas Web',
    // ⭐ Permiso adicional a propósito (pedido de Angelo, 2026-08-05): NO
    // viene incluido con 'caja' -- editar una venta que ya se cobró es un
    // riesgo distinto (puede reducir el total después de que el cliente
    // pagó y se fue), así que el dueño lo asigna aparte solo a quien
    // realmente lo necesite.
    editar_pedidos_pagados: ' Editar pedidos ya pagados',
    // ⭐ FIX (pedido de Angelo, 2026-08-08: "no tengo empleados o
    // asignación de roles en CRM"): estos 2 permisos ya existían y se
    // exigían en el backend (requirePermission('crm') en crmAuth y
    // requirePermission('remarketing') en /api/pos/contacts,
    // /api/pos/customers/:id) desde que se creó el CRM, pero NUNCA
    // tuvieron checkbox acá -- así que ningún empleado, en ninguna
    // cuenta (POS+CRM o CRM-only), pudo recibir acceso al CRM desde la
    // UI hasta ahora. Van separados (no un solo permiso combinado)
    // porque el backend los checkea por separado: 'crm' abre
    // Conversaciones/Workflows/Pipeline/Calendarios/Etiquetas, y
    // 'remarketing' abre Contactos -- un dueño puede querer dar acceso
    // al chat sin exponer la base completa de contactos, o viceversa.
    crm: ' CRM (Conversaciones, Workflows, Pipeline, Calendarios)',
    remarketing: ' Contactos'
  };

  // ⭐ Calcular límite de empleados según plan
  const isCrmOnly = clientData?.productType === 'crm';
  const plan = clientData?.plan || 'basic';
  const empLimit = isCrmOnly
    ? (CRM_EMPLOYEE_LIMITS[clientData?.crmPlanTier] ?? CRM_EMPLOYEE_LIMITS.emprendedor)
    : (PLAN_LIMITS[plan] || PLAN_LIMITS.basic).employees;
  const empCount = employees.length;
  const empPercent = empLimit === Infinity ? 0 : Math.min(100, (empCount / empLimit) * 100);
  const empAtLimit = empLimit !== Infinity && empCount >= empLimit;

  return (
    <div className="p-6">
      {customAlert && (
        <CustomAlert
          type={customAlert.type}
          title={customAlert.title}
          message={customAlert.message}
          onClose={() => setCustomAlert(null)}
          onConfirm={customAlert.onConfirm}
          zIndex={60}
        />
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 rounded-lg">
            <Users className="w-6 h-6 text-purple-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Gestión de Empleados</h1>
            <p className="text-sm text-gray-600">
              {empCount} de {empLimit === Infinity ? 'ilimitados' : empLimit} empleado{empLimit !== 1 ? 's' : ''} · Plan <span className="font-bold capitalize">{plan}</span>
            </p>
          </div>
        </div>

        <button
          onClick={() => {
            if (empAtLimit) {
              showAlert('warning', 'Límite de Plan', `️ Tu plan ${plan.toUpperCase()} permite máximo ${empLimit} empleados. Actualiza tu plan para agregar más.`);
              return;
            }
            setShowModal(true);
          }}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition ${
            empAtLimit ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-purple-600 text-white hover:bg-purple-700'
          }`}
        >
          <Plus className="w-5 h-5" />
          Nuevo Empleado
        </button>
      </div>

      {/* ⭐ Barra de progreso de empleados */}
      {empLimit !== Infinity && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-gray-700">Empleados usados</span>
            <span className={`text-sm font-bold ${empAtLimit ? 'text-red-600' : 'text-purple-600'}`}>
              {empCount} / {empLimit}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className={`h-3 rounded-full transition-all ${
                empPercent >= 100 ? 'bg-red-500' : empPercent >= 75 ? 'bg-yellow-500' : 'bg-purple-500'
              }`}
              style={{ width: `${empPercent}%` }}
            ></div>
          </div>
          {empAtLimit && (
            <p className="text-xs text-red-600 mt-1 font-semibold">
               Límite alcanzado · Actualiza a un plan superior para agregar más empleados
            </p>
          )}
        </div>
      )}
      {empLimit === Infinity && (
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-3 mb-6">
          <p className="text-sm text-purple-700 font-semibold"> Plan Enterprise · Empleados ilimitados</p>
        </div>
      )}

      {/* Lista de empleados */}
      <div className="grid gap-4">
        {employees.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">No hay empleados registrados</p>
            <p className="text-gray-400 text-sm mt-2">Crea el primer empleado para empezar</p>
          </div>
        ) : (
          employees.map((employee) => (
            <div
              key={employee.id}
              className={`bg-white border-2 rounded-lg p-4 ${employee.active ? 'border-gray-200' : 'border-red-300 bg-red-50'}`}
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-bold text-gray-900">{employee.name}</h3>
                    {ROLE_TEMPLATES[employee.role] && (
                      <span className="px-2 py-1 bg-purple-200 text-purple-800 text-xs font-bold rounded-full">
                        {ROLE_TEMPLATES[employee.role].label}
                      </span>
                    )}
                    {!employee.active && (
                      <span className="px-2 py-1 bg-red-200 text-red-800 text-xs font-bold rounded-full">
                        DESACTIVADO
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mb-2">
                     Email: <span className="font-semibold">{employee.email}</span>
                    {employee.phone && <span className="ml-3"> WhatsApp: <span className="font-semibold">{employee.phone}</span></span>}
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {Object.entries(employee.permissions).map(([key, value]) => (
                      value && (
                        <span
                          key={key}
                          className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full"
                        >
                          {permissionLabels[key]}
                        </span>
                      )
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Creado: {new Date(employee.created_at).toLocaleDateString()}
                    {employee.last_login && `  ltimo acceso: ${new Date(employee.last_login).toLocaleString()}`}
                  </p>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => openEditModal(employee)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                  >
                    <Edit2 className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDeleteEmployee(employee)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal Crear/Editar */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b sticky top-0 bg-white z-10">
              <h3 className="text-2xl font-bold">
                {editingEmployee ? 'Editar Empleado' : 'Nuevo Empleado'}
              </h3>
            </div>

            <div className="p-6 space-y-4">
              {/* Nombre */}
              <div>
                <label className="block font-semibold mb-2">
                  Nombre completo <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  value={newEmployee.name}
                  onChange={(e) => setNewEmployee({ ...newEmployee, name: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                  placeholder="Ej: Juan Pérez"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block font-semibold mb-2">
                  Email <span className="text-red-600">*</span>
                </label>
                <input
                  type="email"
                  value={newEmployee.email}
                  onChange={(e) => setNewEmployee({ ...newEmployee, email: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                  placeholder="ejemplo@correo.com"
                  disabled={!!editingEmployee}
                />
              </div>

              {/* ⭐ Teléfono (pedido de Angelo, 2026-08-07): opcional -- solo
                  hace falta si este empleado va a poder elegirse como
                  destinatario del nodo "Notificación interna (WhatsApp)" en
                  Workflows. Sin teléfono, el empleado sigue funcionando
                  normal, simplemente no aparece como opción elegible ahí. */}
              <div>
                <label className="block font-semibold mb-2">
                  Teléfono WhatsApp (opcional)
                </label>
                <input
                  type="tel"
                  value={newEmployee.phone}
                  onChange={(e) => setNewEmployee({ ...newEmployee, phone: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                  placeholder="Ej: 593987654321"
                />
                <p className="text-xs text-gray-500 mt-1">Con código de país, sin espacios ni signos. Se usa solo para notificaciones internas de Workflows.</p>
              </div>

              {/* Contraseña */}
              <div>
                <label className="block font-semibold mb-2">
                  Contraseña {editingEmployee ? '(dejar vacío para mantener)' : <span className="text-red-600">*</span>}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={newEmployee.password}
                    onChange={(e) => setNewEmployee({ ...newEmployee, password: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg pr-12"
                    placeholder="Mínimo 4 caracteres"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Estado */}
              {editingEmployee && (
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={newEmployee.active}
                    onChange={(e) => setNewEmployee({ ...newEmployee, active: e.target.checked })}
                    className="w-5 h-5"
                  />
                  <label className="font-semibold">Cuenta activa</label>
                </div>
              )}

              {/* Plantilla de rol */}
              <div>
                <h4 className="font-semibold mb-1 text-lg">Puesto de trabajo</h4>
                <p className="text-xs text-gray-500 mb-3">
                  Elige un puesto para pre-llenar los permisos típicos. Puedes ajustarlos abajo después.
                  {newEmployee.role === 'cocinero' && ' El Cocinero entra directo a pantalla completa de Cocina, sin ver el resto del sistema.'}
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {Object.entries(ROLE_TEMPLATES).map(([key, tpl]) => (
                    <button
                      type="button"
                      key={key}
                      onClick={() => applyRoleTemplate(key)}
                      className={`p-3 border-2 rounded-lg text-left transition ${
                        newEmployee.role === key
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-gray-300 bg-gray-50 hover:border-gray-400'
                      }`}
                    >
                      <p className="font-semibold text-sm">{tpl.label}</p>
                      <p className="text-xs text-gray-500">{tpl.description}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Permisos */}
              <div>
                <h4 className="font-semibold mb-3 text-lg">Permisos de acceso</h4>
                <div className="grid grid-cols-2 gap-3">
                  {Object.keys(permissionLabels).map((key) => (
                    <div
                      key={key}
                      onClick={() => togglePermission(key)}
                      className={`p-3 border-2 rounded-lg cursor-pointer transition ${
                        newEmployee.permissions[key]
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-gray-300 bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={newEmployee.permissions[key]}
                          readOnly
                          className="w-5 h-5"
                        />
                        <span className="font-semibold text-sm">{permissionLabels[key]}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="p-6 border-t flex justify-end gap-3">
              <button
                onClick={closeModal}
                className="px-6 py-2 border-2 border-gray-300 text-gray-700 font-bold rounded-lg hover:bg-gray-100 transition"
              >
                Cancelar
              </button>
              <button
                onClick={editingEmployee ? handleUpdateEmployee : handleCreateEmployee}
                className="px-6 py-2 bg-purple-600 text-white font-bold rounded-lg hover:bg-purple-700 transition"
              >
                {editingEmployee ? 'Guardar Cambios' : 'Crear Empleado'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EmpleadosManager;
